import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  TextField,
  Button,
  Banner,
  Divider,
  ColorPicker,
  hsbToHex,
  InlineStack,
  Checkbox,
  Badge,
} from '@shopify/polaris';
import { api } from '../utils/api';

interface ShopSettings {
  trackingPagePrimaryColor?: string;
  emailEnabled?: boolean;
  notificationFromEmail?: string;
  notificationFromName?: string;
  smsEnabled?: boolean;
}

// Simple hex to HSB conversion
function hexToHsb(hex: string): { hue: number; saturation: number; brightness: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  return {
    hue: h * 360,
    saturation: max === 0 ? 0 : d / max,
    brightness: max,
  };
}

export default function Settings() {
  // Fetch current settings
  const { data: currentSettings } = useQuery<ShopSettings>({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings'),
  });

  // Tracking page settings
  const [primaryColor, setPrimaryColor] = useState({ hue: 220, saturation: 0.7, brightness: 0.6 });
  
  // Email settings
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  
  // SMS settings
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');

  // API Keys
  const [laPosteKey, setLaPosteKey] = useState('');

  const [saved, setSaved] = useState(false);
  const [testSmsResult, setTestSmsResult] = useState<{ success: boolean; message: string } | null>(null);

  // Load current settings
  useEffect(() => {
    if (currentSettings) {
      if (currentSettings.trackingPagePrimaryColor) {
        try {
          setPrimaryColor(hexToHsb(currentSettings.trackingPagePrimaryColor));
        } catch (e) { /* ignore */ }
      }
      setEmailEnabled(currentSettings.emailEnabled ?? true);
      setFromEmail(currentSettings.notificationFromEmail || '');
      setFromName(currentSettings.notificationFromName || '');
      setSmsEnabled(currentSettings.smsEnabled ?? false);
    }
  }, [currentSettings]);

  const saveMutation = useMutation({
    mutationFn: (settings: any) => api.put('/settings', settings),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const handleSave = useCallback(() => {
    saveMutation.mutate({
      trackingPagePrimaryColor: hsbToHex(primaryColor),
      emailEnabled,
      notificationFromEmail: fromEmail,
      notificationFromName: fromName,
      smsEnabled,
      laPosteApiKey: laPosteKey || undefined,
    });
  }, [primaryColor, emailEnabled, fromEmail, fromName, smsEnabled, laPosteKey, saveMutation]);

  const testSmsMutation = useMutation({
    mutationFn: (phone: string) => api.post('/notifications/test-sms', { phone }),
    onSuccess: () => {
      setTestSmsResult({ success: true, message: 'SMS de test envoyé avec succès!' });
    },
    onError: (error: any) => {
      setTestSmsResult({ success: false, message: error.message || 'Erreur lors de l\'envoi' });
    },
  });

  return (
    <Page title="Paramètres">
      <Layout>
        {saved && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSaved(false)}>
              Paramètres enregistrés avec succès
            </Banner>
          </Layout.Section>
        )}

        {/* Tracking Page Customization */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                🎨 Page de suivi
              </Text>
              <Text as="p" tone="subdued">
                Personnalisez l'apparence de la page de suivi pour vos clients.
              </Text>
              <Divider />

              <BlockStack gap="200">
                <Text as="span" variant="bodyMd">
                  Couleur principale
                </Text>
                <ColorPicker onChange={setPrimaryColor} color={primaryColor} />
                <Text as="span" tone="subdued" variant="bodySm">
                  {hsbToHex(primaryColor)}
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Email Notifications - SIMPLIFIED */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  📧 Notifications email
                </Text>
                <Badge tone={emailEnabled ? 'success' : 'critical'}>
                  {emailEnabled ? 'Activé' : 'Désactivé'}
                </Badge>
              </InlineStack>
              
              <Banner tone="info">
                Les emails sont envoyés automatiquement à l'adresse email du client (depuis sa commande Shopify).
                Aucune configuration technique n'est requise.
              </Banner>
              
              <Divider />
              
              <Checkbox
                label="Activer les notifications par email"
                checked={emailEnabled}
                onChange={setEmailEnabled}
                helpText="Les clients recevront des emails lors des mises à jour de leur colis"
              />

              <TextField
                label="Nom affiché de l'expéditeur"
                value={fromName}
                onChange={setFromName}
                autoComplete="off"
                placeholder="Ma Boutique"
                helpText="Le nom qui apparaîtra comme expéditeur des emails"
              />
              
              <TextField
                label="Email de réponse (optionnel)"
                type="email"
                value={fromEmail}
                onChange={setFromEmail}
                autoComplete="off"
                placeholder="contact@ma-boutique.com"
                helpText="Si le client répond à l'email, il sera envoyé à cette adresse"
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* SMS Notifications */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  📱 Notifications SMS
                </Text>
                <Badge tone={smsEnabled ? 'success' : 'critical'}>
                  {smsEnabled ? 'Activé' : 'Désactivé'}
                </Badge>
              </InlineStack>
              
              <Text as="p" tone="subdued">
                Envoyez des SMS aux clients lors des étapes importantes (expédition, livraison).
              </Text>
              
              <Divider />
              
              <Checkbox
                label="Activer les notifications SMS"
                checked={smsEnabled}
                onChange={setSmsEnabled}
              />
              
              {smsEnabled && (
                <BlockStack gap="300">
                  <Banner tone="info">
                    Les SMS sont envoyés au numéro de téléphone du client (depuis sa commande Shopify).
                    Coût inclus dans votre abonnement TrackFlow (limité selon votre plan).
                  </Banner>
                  
                  {testSmsResult && (
                    <Banner 
                      tone={testSmsResult.success ? 'success' : 'critical'} 
                      onDismiss={() => setTestSmsResult(null)}
                    >
                      {testSmsResult.message}
                    </Banner>
                  )}
                  
                  <TextField
                    label="Tester les SMS"
                    value={testPhoneNumber}
                    onChange={setTestPhoneNumber}
                    autoComplete="off"
                    placeholder="+33612345678"
                    helpText="Entrez votre numéro pour recevoir un SMS de test"
                  />
                  <Button
                    onClick={() => testSmsMutation.mutate(testPhoneNumber)}
                    loading={testSmsMutation.isPending}
                    disabled={!testPhoneNumber}
                  >
                    Envoyer un SMS de test
                  </Button>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Carrier API Keys */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                🚚 Transporteurs
              </Text>
              <Text as="p" tone="subdued">
                Connectez vos comptes transporteurs pour un suivi en temps réel plus précis.
              </Text>
              <Divider />

              <TextField
                label="Clé API La Poste / Colissimo"
                value={laPosteKey}
                onChange={setLaPosteKey}
                autoComplete="off"
                type="password"
                placeholder="Votre clé API"
                helpText="Optionnel - Obtenez une clé gratuite sur developer.laposte.fr"
              />
              
              <Banner tone="info">
                D'autres transporteurs (Chronopost, DHL, UPS, Mondial Relay) seront disponibles prochainement.
              </Banner>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Save Button */}
        <Layout.Section>
          <InlineStack align="end">
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saveMutation.isPending}
            >
              Enregistrer les paramètres
            </Button>
          </InlineStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
